import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseConfig';
import AuthPage from './AuthPage';

import { normalize, storageKey } from "./utils/tourUtils";
import { useProgress } from "./hooks/useProgress";
import { useMissionTimer } from "./hooks/useMissionTimer";
import { Card } from "./components/ui";
import { ClueCard } from "./components/tour";
import { MissionWelcome } from "./components/intro/MissionWelcome";
import { MissionBriefing } from "./components/intro/MissionBriefing";
import { IncomingTransmission } from "./components/intro/IncomingTransmission";
import { MissionQuickTips } from "./components/intro/MissionQuickTips";
import { CoordinateDecoder } from "./components/tour/CoordinateDecoder";
import { FeedbackSurvey } from "./components/tour/FeedbackSurvey";
import { SpyJournalModal } from "./components/tour/SpyJournalModal";
import { radius } from "./utils/designSystem";

/**
 * Mission Tour App Component
 * Handles mission-style tours with timer, story interludes, and tactical UI
 */
export default function MissionTourApp({ tourData }) {
  const [authStatus, setAuthStatus] = useState("checking");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        setAuthStatus("in");
      } else {
        setAuthStatus("out");
      }
    });
    return unsub;
  }, []);

  if (authStatus === "checking") {
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-sm text-gray-300">
          Establishing secure connection…
        </div>
      </div>
    );
  }

  if (authStatus === "out") {
    return <AuthPage />;
  }

  return <MissionTourInner tour={tourData} />;
}

function MissionTourInner({ tour }) {
  const progressHook = useProgress(tour.slug, tour.steps);
  const transmissionKey = `${tour.slug}_transmission_shown`;
  const briefingKey = `${tour.slug}_briefing_shown`;
  const quickTipsKey = `${tour.slug}_quick_tips_shown`;

  const hasStarted = progressHook.progress.answers && Object.keys(progressHook.progress.answers).length > 0;
  const transmissionShown = localStorage.getItem(transmissionKey);
  const briefingShown = localStorage.getItem(briefingKey);
  const quickTipsShown = localStorage.getItem(quickTipsKey);

  // Show welcome first if tour hasn't started
  const [showWelcome, setShowWelcome] = useState(() => !hasStarted);

  // Don't show transmission/briefing until user clicks "Begin Mission"
  const [showTransmission, setShowTransmission] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);

  const [showTracker, setShowTracker] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [justSolvedId, setJustSolvedId] = useState(null);
  const [showInterlude, setShowInterlude] = useState(false);
  const [currentInterlude, setCurrentInterlude] = useState(null);
  const [shownInterludes, setShownInterludes] = useState(new Set());
  const [showMissionComplete, setShowMissionComplete] = useState(false);
  const [showEpilogue, setShowEpilogue] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showQuickTips, setShowQuickTips] = useState(false);
  const [showTransmissionAnimation, setShowTransmissionAnimation] = useState(false);

  // Timer hook - only active after all intro screens (welcome, transmission, briefing)
  const timer = useMissionTimer(
    tour.suggestedTimeLimit || 180,
    tour.slug,
    !showWelcome && !showTransmission && !showBriefing && !showMissionComplete
  );

  const current = progressHook.current;
  const currentIndex = progressHook.progress.index;
  const atEnd = currentIndex >= tour.steps.length - 1;

  // Count solved steps
  const solvedCount = Object.keys(progressHook.progress.answers || {}).filter((stepId) => {
    const step = tour.steps.find(s => s.id === stepId);
    if (!step) return false;
    return progressHook.isAnsweredCorrect(step) === true;
  }).length;

  // Check for story interludes when location is solved
  useEffect(() => {
    if (tour.storyInterludes && solvedCount > 0) {
      const interlude = tour.storyInterludes.find(
        (item) => item.triggerAfterLocation === solvedCount
      );
      // Only show if interlude exists, not already shown, and modal not currently open
      if (interlude && !shownInterludes.has(solvedCount) && !showInterlude) {
        setCurrentInterlude(interlude);
        setShowInterlude(true);
        setShownInterludes(prev => new Set([...prev, solvedCount]));
      }
    }
  }, [solvedCount, tour.storyInterludes, shownInterludes, showInterlude]);

  // Show quick tips on first location if not shown before
  useEffect(() => {
    // Only show if:
    // 1. We're on the first location (index 0)
    // 2. Quick tips haven't been shown before
    // 3. All intro screens are done
    // 4. Not already showing tips
    if (
      currentIndex === 0 &&
      !quickTipsShown &&
      !showWelcome &&
      !showTransmission &&
      !showBriefing &&
      !showQuickTips
    ) {
      // Small delay so user sees the location first
      const timer = setTimeout(() => {
        setShowQuickTips(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, quickTipsShown, showWelcome, showTransmission, showBriefing, showQuickTips]);

  const beginMission = () => {
    // User clicked "Begin Mission" on welcome screen
    setShowWelcome(false);

    // Check if we need to show intro sequence
    if (!transmissionShown) {
      // Show transmission first
      setShowTransmission(true);
    } else if (!briefingShown) {
      // Transmission already shown, show briefing
      setShowBriefing(true);
    }
    // If both already shown, tour starts automatically (neither state is true)
  };

  const acceptTransmission = () => {
    // Mark transmission as shown, move to briefing
    localStorage.setItem(transmissionKey, 'true');
    setShowTransmission(false);
    setShowBriefing(true);
  };

  const completeBriefing = () => {
    // Mark briefing as shown, start tour
    localStorage.setItem(briefingKey, 'true');
    setShowBriefing(false);
    // Tour starts automatically (all intro states false)
  };

  const submitAnswer = (stepId, answer) => {
    progressHook.submitAnswer(stepId, answer);

    const step = tour.steps.find((s) => s.id === stepId);
    if (!step) return;

    const isCorrect = step.acceptedAnswers.some(
      (a) => normalize(a) === normalize(answer)
    );

    if (isCorrect) {
      setCorrect(true);
      setJustSolvedId(stepId);

      // For attribute collection tours (Chicago), skip the journal popup at final answer
      // and go directly to transmission animation
      if (atEnd && tour.collectionMode === "attributes") {
        setTimeout(() => {
          setShowTransmissionAnimation(true);
        }, 300);
      } else {
        setShowTracker(true);
      }
    } else {
      setCorrect(false);
    }
  };

  const next = () => {
    // Allow advancing if current step is solved (either just now or previously)
    const currentStepSolved = correct === true || progressHook.isAnsweredCorrect(current) === true;
    if (currentStepSolved && !atEnd) {
      progressHook.next();
      setCorrect(null);
    }
  };

  const closeQuickTips = () => {
    setShowQuickTips(false);
    localStorage.setItem(quickTipsKey, 'true');
  };

  const handleDontShowAgain = () => {
    localStorage.setItem(quickTipsKey, 'true');
  };

  // Show welcome screen first
  if (showWelcome) {
    return <MissionWelcome tour={tour} onStart={beginMission} />;
  }

  // Show incoming transmission after "Begin Mission" clicked (only once per tour)
  if (showTransmission) {
    return <IncomingTransmission tour={tour} onAccept={acceptTransmission} />;
  }

  // Show briefing after transmission (only once per tour)
  if (showBriefing) {
    return <MissionBriefing tour={tour} onComplete={completeBriefing} />;
  }

  // Show feedback survey after mission complete
  if (showFeedback) {
    return (
      <FeedbackSurvey
        tourSlug={tour.slug}
        onComplete={() => {
          // Mark tour as finished in progress data
          const progressKey = storageKey(tour.slug);
          const progressData = localStorage.getItem(progressKey);
          if (progressData) {
            try {
              const progress = JSON.parse(progressData);
              progress.finished = true;
              localStorage.setItem(progressKey, JSON.stringify(progress));
              console.log('[MissionTourApp] ✅ Marked tour as finished:', progressKey);
            } catch (err) {
              console.error('[MissionTourApp] Error updating progress:', err);
            }
          } else {
            console.warn('[MissionTourApp] No progress data found to mark as finished:', progressKey);
          }

          // Set badge earned flag (regardless of whether user submitted or skipped feedback)
          localStorage.setItem('badgeEarned', tour.slug);
          console.log('[MissionTourApp] ✅ Set badgeEarned flag:', tour.slug);

          // Redirect to home page after feedback
          window.location.href = '/';
        }}
      />
    );
  }

  // Show transmission animation before mission complete
  if (showTransmissionAnimation) {
    return (
      <TransmissionAnimation
        finalAnswer={tour.finalAnswer || 'XOCHIMILCO'}
        timer={timer}
        tour={tour}
        onComplete={() => {
          setShowTransmissionAnimation(false);
          setShowFeedback(true); // Go directly to Feedback, skip Epilogue
        }}
      />
    );
  }

  // Show historical epilogue after mission complete
  if (showEpilogue) {
    return (
      <HistoricalEpilogueScreen
        tour={tour}
        onNext={() => setShowFeedback(true)}
      />
    );
  }

  // Show mission complete at the end
  if (showMissionComplete) {
    return (
      <MissionCompleteScreen
        tour={tour}
        timer={timer}
        onNext={() => setShowEpilogue(true)}
      />
    );
  }

  // Timer urgency colors
  const getTimerColor = () => {
    switch (timer.urgency) {
      case 'critical': return '#EF4444'; // red
      case 'warning': return '#F97316'; // orange
      case 'caution': return '#F59E0B'; // yellow
      default: return '#10B981'; // emerald
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 font-body">
      <style>{`
        .font-libre { font-family: 'Crimson Pro', Georgia, serif; }
        .font-body { font-family: 'Plus Jakarta Sans', Inter, system-ui, -apple-system, sans-serif; }
        .slide-in { animation: slideIn 380ms cubic-bezier(.2,.8,.2,1); }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(8px);}
          to { opacity: 1; transform: translateY(0); }
        }
        .pulse-glow {
          animation: pulseGlow 2s ease-in-out infinite;
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px ${getTimerColor()}40; }
          50% { box-shadow: 0 0 30px ${getTimerColor()}60; }
        }
        /* Ensure background extends behind notch/Dynamic Island on iOS */
        body {
          background-color: #0f172a;
        }
        html {
          background-color: #0f172a;
        }
      `}</style>

      {/* Mission Header with Timer */}
      <header className="z-10 border-b border-emerald-500/20 bg-slate-900/80 backdrop-blur-md sticky top-0" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="mx-auto w-full max-w-xl px-4 py-3">
          <div className="flex items-center gap-3 justify-between">
            {/* Left: Menu dropdown */}
            <div className="relative shrink-0">
              <button
                onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                className="px-3 py-1.5 rounded-lg flex items-center gap-1 justify-center transition-all text-xs font-semibold uppercase tracking-wide"
                style={{
                  background: 'rgba(71, 85, 105, 0.3)',
                  border: '1px solid rgba(71, 85, 105, 0.5)',
                  color: '#ffffff'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(71, 85, 105, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(71, 85, 105, 0.3)';
                }}
                title="Menu"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
                Menu
              </button>

              {/* Dropdown menu */}
              {showMenuDropdown && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMenuDropdown(false)}
                  />
                  <div
                    className="absolute top-full left-0 mt-2 w-56 rounded-lg overflow-hidden shadow-2xl z-50"
                    style={{
                      background: '#1e293b',
                      border: '1px solid rgba(71, 85, 105, 0.5)',
                    }}
                  >
                    <button
                      onClick={() => {
                        setShowMenuDropdown(false);
                        setShowExitConfirm(true);
                      }}
                      className="w-full px-4 py-3 text-left text-sm font-medium text-gray-200 hover:bg-slate-700 transition-colors flex items-center gap-3"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                      Exit and Save Progress
                    </button>
                    <div style={{ height: '1px', background: 'rgba(71, 85, 105, 0.5)' }} />
                    <button
                      onClick={() => {
                        setShowMenuDropdown(false);
                        setShowStartOverConfirm(true);
                      }}
                      className="w-full px-4 py-3 text-left text-sm font-medium text-gray-200 hover:bg-slate-700 transition-colors flex items-center gap-3"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="1 4 1 10 7 10"></polyline>
                        <polyline points="23 20 23 14 17 14"></polyline>
                        <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                      </svg>
                      Start Over
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Center: Mission name */}
            <div className="flex items-center gap-3 min-w-0 flex-1 justify-center">
              <div className="min-w-0 text-center">
                <div className="text-[9px] sm:text-xs font-semibold uppercase tracking-wider text-emerald-400">
                  {tour.missionName || tour.title}
                </div>
                <div className="truncate font-libre font-bold text-sm sm:text-base text-white">
                  {tour.city}
                </div>
              </div>
            </div>

            {/* Right: Timer display */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Pause button */}
              <button
                onClick={() => {
                  if (timer.isPaused) {
                    timer.resumeTimer();
                    setShowPauseModal(false);
                  } else {
                    timer.pauseTimer();
                    setShowPauseModal(true);
                  }
                }}
                className="px-2 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: timer.isPaused ? 'rgba(16, 185, 129, 0.2)' : 'rgba(71, 85, 105, 0.3)',
                  border: `1px solid ${timer.isPaused ? 'rgba(16, 185, 129, 0.4)' : 'rgba(71, 85, 105, 0.5)'}`,
                  color: timer.isPaused ? '#10B981' : '#ffffff'
                }}
                title={timer.isPaused ? 'Resume' : 'Pause'}
              >
                {timer.isPaused ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                )}
              </button>

              {/* Relief button - only show when eligible */}
              {timer.canUseRelief && (
                <button
                  onClick={timer.addReliefTime}
                  className="px-2 py-1 rounded-md text-xs font-semibold transition-all"
                  style={{
                    background: 'rgba(16, 185, 129, 0.2)',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    color: '#10B981'
                  }}
                  title="Add 30 minutes (one-time)"
                >
                  +30min
                </button>
              )}

              {/* Timer display */}
              <div
                className={`px-3 py-1.5 rounded-lg font-mono text-sm font-bold ${
                  timer.urgency === 'critical' && !timer.isPaused ? 'pulse-glow' : ''
                }`}
                style={{
                  background: timer.isPaused ? 'rgba(251, 191, 36, 0.2)' : `${getTimerColor()}20`,
                  border: timer.isPaused ? '1px solid rgba(251, 191, 36, 0.4)' : `1px solid ${getTimerColor()}40`,
                  color: timer.isPaused ? '#FBBF24' : getTimerColor()
                }}
              >
                {timer.isPaused ? 'PAUSED' : timer.hasExpired ? 'EXPIRED' : timer.formattedTime}
              </div>
            </div>
          </div>

          {/* Coordinate tracker - mini version */}
          {tour.finalAnswer && (
            <div className="mt-3 mb-1">
              <div className="flex justify-center gap-0.5 sm:gap-1">
                {Array.from({ length: tour.finalAnswerLength || 10 }).map((_, idx) => {
                  // Build coordinate grid from solved steps
                  const solvedSteps = tour.steps.filter(step => progressHook.isAnsweredCorrect(step) === true);
                  const letterAtPosition = solvedSteps.find(step => step.secretIndex === idx);
                  const letter = letterAtPosition ? letterAtPosition.revealedLetter : '_';
                  const isFilled = letter !== '_';

                  return (
                    <div
                      key={idx}
                      className="w-5 h-6 sm:w-6 sm:h-7 flex items-center justify-center rounded border font-mono font-bold transition-all duration-300"
                      style={{
                        borderColor: isFilled ? '#10B981' : '#334155',
                        backgroundColor: isFilled ? 'rgba(16, 185, 129, 0.15)' : 'rgba(51, 65, 85, 0.2)',
                        color: isFilled ? '#10B981' : '#64748b',
                        fontSize: '0.7rem',
                        letterSpacing: '0.02em'
                      }}
                    >
                      {letter}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div className="flex items-center gap-2 mt-2">
            <div className="flex flex-1 gap-1">
              {tour.steps.map((step, idx) => {
                const solved = progressHook.isAnsweredCorrect(step) === true;
                const isCurrent = idx === currentIndex;
                return (
                  <div
                    key={step.id}
                    className="relative h-2 flex-1 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: solved ? '#10B981' : '#334155',
                      boxShadow: isCurrent ? `0 0 0 2px #0F172A, 0 0 0 4px #10B981` : 'none',
                    }}
                  />
                );
              })}
            </div>
            <div className="text-xs font-semibold whitespace-nowrap text-gray-400">
              {solvedCount}/{tour.steps.length}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-xl space-y-6 px-4 pb-28 pt-6">
        {/* Location image card */}
        <Card padding="none" className="overflow-hidden">
          {current.image && (
            <div className="relative overflow-hidden" style={{ borderRadius: radius.card }}>
              <img
                src={current.image}
                alt={`Location ${currentIndex + 1}`}
                className="h-80 md:h-96 w-full object-cover"
              />
              {/* Overlay card with mission styling */}
              <div
                className="absolute bottom-0 left-0 right-0 p-5 md:p-6"
                style={{
                  background: 'rgba(15, 23, 42, 0.9)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderBottomLeftRadius: radius.card,
                  borderBottomRightRadius: radius.card,
                }}
              >
                <div className="space-y-1">
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                    Location {currentIndex + 1} of {tour.steps.length}
                  </div>
                  <div
                    className="text-2xl md:text-3xl font-extrabold leading-tight text-white"
                    style={{ fontFamily: 'Rubik, sans-serif' }}
                  >
                    {current.title}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Clue card with mission mode enabled */}
        <div key={current.id} className="slide-in">
          <ClueCard
            step={current}
            isRevealed={progressHook.isRevealed}
            reveal={progressHook.reveal}
            isCorrect={correct}
            onSubmit={(val) => submitAnswer(current.id, val)}
            letterMask={progressHook.getLetterMask(current)}
            onRevealLetter={() => progressHook.revealNextLetter(current)}
            isMissionMode={true}
            targetCoordinates={current.coordinates}
            tour={tour}
            progressHook={progressHook}
            isFinalStep={atEnd}
          />
        </div>
      </main>

      {/* Footer with navigation buttons */}
      <footer className="fixed inset-x-0 bottom-0 border-t border-emerald-500/20 bg-slate-900/90 backdrop-blur-md">
        <div className="mx-auto max-w-xl items-center gap-3 px-4 py-3">
          <div className="py-2 flex items-center gap-2">
            {/* Previous button - subtle, only show if not on first step */}
            {currentIndex > 0 && (
              <button
                onClick={() => {
                  progressHook.prev();
                  setCorrect(null);
                }}
                className="px-3 py-3 rounded-xl transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(71, 85, 105, 0.3)',
                  border: '1px solid rgba(71, 85, 105, 0.5)',
                  color: '#9CA3AF'
                }}
                title="Previous location"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
            )}

            {/* Next/Complete button */}
            {(() => {
              // Check if current step is already solved (either just now or previously)
              const currentStepSolved = correct === true || progressHook.isAnsweredCorrect(current) === true;

              return (
                <>
                  {!atEnd && (
                    <button
                      onClick={next}
                      disabled={!currentStepSolved}
                      className="flex-1 px-4 py-3 text-base font-semibold rounded-xl transition-all"
                      style={{
                        background: currentStepSolved
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          : '#334155',
                        color: '#ffffff',
                        border: 'none',
                        cursor: currentStepSolved ? 'pointer' : 'not-allowed',
                        opacity: currentStepSolved ? 1 : 0.5
                      }}
                    >
                      Next Location →
                    </button>
                  )}
                  {atEnd && currentStepSolved && (
                    <button
                      onClick={() => {
                        // For attribute tours, go to TransmissionAnimation (clean debrief)
                        // For other tours, go to MissionCompleteScreen
                        if (tour.collectionMode === "attributes") {
                          setShowTransmissionAnimation(true);
                        } else {
                          setShowMissionComplete(true);
                        }
                      }}
                      className="flex-1 px-4 py-3 text-base font-semibold rounded-xl transition-all"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#ffffff',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      Complete Mission →
                    </button>
                  )}
                  {atEnd && !currentStepSolved && (
                    <div className="flex-1 px-4 py-3 text-base font-semibold rounded-xl text-center"
                      style={{
                        background: '#334155',
                        color: '#ffffff',
                        opacity: 0.5
                      }}
                    >
                      Complete Mission →
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </footer>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && ReactDOM.createPortal(
        <ExitConfirmModal
          onCancel={() => setShowExitConfirm(false)}
          onConfirm={() => window.location.href = '/'}
        />,
        document.body
      )}

      {/* Start Over Confirmation Modal */}
      {showStartOverConfirm && ReactDOM.createPortal(
        <StartOverConfirmModal
          onCancel={() => setShowStartOverConfirm(false)}
          onConfirm={() => {
            // Clear tour progress
            progressHook.reset();
            // Clear intro screens flags so they show again
            localStorage.removeItem(transmissionKey);
            localStorage.removeItem(briefingKey);
            // Reset timer
            timer.resetTimer();
            // Close modal and restart
            setShowStartOverConfirm(false);
            setShowWelcome(true);
          }}
        />,
        document.body
      )}

      {/* Story Interlude Modal */}
      {showInterlude && currentInterlude && ReactDOM.createPortal(
        <StoryInterludeModal
          interlude={currentInterlude}
          onClose={() => setShowInterlude(false)}
        />,
        document.body
      )}

      {/* Answer Tracker Modal - Use SpyJournalModal for attribute collection mode (Chicago) */}
      {showTracker && ReactDOM.createPortal(
        tour.collectionMode === "attributes" ? (
          <SpyJournalModal
            tour={tour}
            progressHook={progressHook}
            justSolvedId={justSolvedId}
            onClose={(triggerTransmission = false) => {
              setShowTracker(false);
              // If this was the last location, show transmission animation (clean debrief)
              if (atEnd) {
                setTimeout(() => {
                  setShowTransmissionAnimation(true);
                }, 300);
              }
            }}
          />
        ) : (
          <AnswerTrackerModal
            tour={tour}
            progressHook={progressHook}
            justSolvedId={justSolvedId}
            onClose={(triggerTransmission = false) => {
              setShowTracker(false);
              // If this was the last location and transmission triggered, show transmission animation
              if (atEnd && triggerTransmission) {
                setTimeout(() => {
                  setShowTransmissionAnimation(true);
                }, 300);
              } else if (atEnd) {
                // Fallback: show mission complete directly if no transmission
                setTimeout(() => {
                  setShowMissionComplete(true);
                }, 300);
              }
            }}
          />
        ),
        document.body
      )}

      {/* Pause Modal */}
      {showPauseModal && timer.isPaused && ReactDOM.createPortal(
        <PauseModal
          timer={timer}
          onResume={() => {
            timer.resumeTimer();
            setShowPauseModal(false);
          }}
        />,
        document.body
      )}

      {/* Quick Tips Modal - shows on first location */}
      {showQuickTips && ReactDOM.createPortal(
        <MissionQuickTips
          onClose={closeQuickTips}
          onDontShowAgain={handleDontShowAgain}
          tour={tour}
        />,
        document.body
      )}
    </div>
  );
}

/**
 * Exit Confirmation Modal - warns user about leaving mission in progress
 */
function ExitConfirmModal({ onCancel, onConfirm }) {
  return (
    <div
      className="fixed z-50 flex items-center justify-center"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(8px)',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1rem) 1rem calc(env(safe-area-inset-bottom, 0px) + 1rem)'
      }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          background: '#1e293b',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          boxShadow: '0 0 40px rgba(239, 68, 68, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Warning icon */}
          <div className="flex items-center justify-center mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '2px solid rgba(239, 68, 68, 0.5)'
              }}
            >
              ⚠️
            </div>
          </div>

          {/* Title */}
          <h2
            className="text-center font-mono text-xl font-bold mb-2 tracking-wider"
            style={{ color: '#EF4444' }}
          >
            EXIT MISSION
          </h2>

          {/* Warning text */}
          <div className="text-center text-xs font-semibold uppercase tracking-widest mb-4 text-gray-400">
            Warning
          </div>

          {/* Message */}
          <div
            className="p-4 rounded-lg mb-6"
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)'
            }}
          >
            <p className="text-sm text-gray-300 mb-3">
              Are you sure you want to exit this mission?
            </p>
            <p className="text-sm text-gray-300">
              Your progress has been saved and you can resume later from where you left off.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all"
              style={{
                background: 'rgba(71, 85, 105, 0.5)',
                color: '#ffffff',
                border: '1px solid rgba(71, 85, 105, 0.8)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(71, 85, 105, 0.7)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(71, 85, 105, 0.5)';
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide"
              style={{
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Exit Mission
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Start Over Confirmation Modal - warns user about resetting progress
 */
function StartOverConfirmModal({ onCancel, onConfirm }) {
  return (
    <div
      className="fixed z-50 flex items-center justify-center"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(8px)',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1rem) 1rem calc(env(safe-area-inset-bottom, 0px) + 1rem)'
      }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          background: '#1e293b',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          boxShadow: '0 0 40px rgba(239, 68, 68, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Warning icon */}
          <div className="flex items-center justify-center mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '2px solid rgba(239, 68, 68, 0.5)'
              }}
            >
              🔄
            </div>
          </div>

          {/* Title */}
          <h2
            className="text-center font-mono text-xl font-bold mb-2 tracking-wider"
            style={{ color: '#EF4444' }}
          >
            START OVER
          </h2>

          {/* Warning text */}
          <div className="text-center text-xs font-semibold uppercase tracking-widest mb-4 text-gray-400">
            Warning
          </div>

          {/* Message */}
          <div
            className="p-4 rounded-lg mb-6"
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)'
            }}
          >
            <p className="text-sm text-gray-300 mb-3">
              Are you sure you want to restart this mission from the beginning?
            </p>
            <p className="text-sm text-gray-300">
              All your current progress will be lost and cannot be recovered.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all"
              style={{
                background: 'rgba(71, 85, 105, 0.5)',
                color: '#ffffff',
                border: '1px solid rgba(71, 85, 105, 0.8)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(71, 85, 105, 0.7)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(71, 85, 105, 0.5)';
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide"
              style={{
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Story Interlude Modal - displays mission updates
 */
function StoryInterludeModal({ interlude, onClose }) {
  return (
    <div
      className="fixed z-50 flex items-center justify-center"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(8px)',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1rem) 1rem calc(env(safe-area-inset-bottom, 0px) + 1rem)'
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          background: '#1e293b',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          boxShadow: '0 0 40px rgba(239, 68, 68, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Icon and Priority */}
          <div className="flex items-center justify-center mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '2px solid rgba(239, 68, 68, 0.5)'
              }}
            >
              {interlude.icon || '⚠️'}
            </div>
          </div>

          {/* Title */}
          <h2
            className="text-center font-mono text-xl font-bold mb-2 tracking-wider"
            style={{ color: '#EF4444' }}
          >
            {interlude.title || 'INCOMING TRANSMISSION'}
          </h2>

          {/* Priority level */}
          <div className="text-center text-xs font-semibold uppercase tracking-widest mb-4 text-gray-400">
            Priority: {interlude.priority || 'high'}
          </div>

          {/* Message */}
          <div
            className="p-4 rounded-lg mb-6"
            style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}
          >
            <p className="text-sm text-gray-300 whitespace-pre-line">
              {interlude.message}
            </p>
          </div>

          {/* Acknowledge button */}
          <button
            onClick={onClose}
            className="w-full py-3 rounded-lg font-semibold text-sm uppercase tracking-wide"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              border: 'none'
            }}
          >
            Acknowledged
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Pause Modal - displays when game is paused
 */
function PauseModal({ timer, onResume }) {
  return (
    <div
      className="fixed z-50 flex items-center justify-center"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(12px)',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1rem) 1rem calc(env(safe-area-inset-bottom, 0px) + 1rem)'
      }}
    >
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          background: '#1e293b',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          boxShadow: '0 0 40px rgba(251, 191, 36, 0.2)'
        }}
      >
        <div className="p-6">
          {/* Pause icon */}
          <div className="flex items-center justify-center mb-4">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(251, 191, 36, 0.2)',
                border: '3px solid rgba(251, 191, 36, 0.5)'
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#FBBF24">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h2
            className="text-center font-mono text-2xl font-bold mb-2 tracking-wider"
            style={{ color: '#FBBF24' }}
          >
            MISSION PAUSED
          </h2>

          {/* Time remaining */}
          <div className="text-center mb-6">
            <div className="text-xs font-semibold uppercase tracking-widest mb-2 text-gray-400">
              Time Remaining
            </div>
            <div
              className="font-mono text-3xl font-bold"
              style={{ color: '#FBBF24' }}
            >
              {timer.formattedTime}
            </div>
          </div>

          {/* Message */}
          <div
            className="p-4 rounded-lg mb-6"
            style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.2)'
            }}
          >
            <p className="text-sm text-gray-300 text-center">
              The timer has been paused. Take your time and resume when you're ready to continue the mission.
            </p>
          </div>

          {/* Resume button */}
          <button
            onClick={onResume}
            className="w-full py-3 rounded-lg font-semibold text-sm uppercase tracking-wide"
            style={{
              background: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
              color: '#000000',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
            }}
          >
            <span className="flex items-center justify-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Resume Mission
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mission Complete with Location Decoded - professional celebration screen
 */
function TransmissionAnimation({ finalAnswer, timer, tour, onComplete }) {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const voiceoverTextRef = useRef(null);

  // Pause the timer when component mounts
  useEffect(() => {
    if (timer && timer.pauseTimer) {
      console.log('[TransmissionAnimation] Pausing timer');
      timer.pauseTimer();
    }
  }, [timer]);

  // Format timer display - show time elapsed
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Audio control functions
  const toggleAudio = () => {
    if (!audioRef.current) return;

    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      audioRef.current.play().catch((err) => {
        console.error('Audio playback failed:', err);
      });
      setIsAudioPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsAudioPlaying(false);
    setAudioCurrentTime(0);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setAudioCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * audioDuration;
    audioRef.current.currentTime = newTime;
    setAudioCurrentTime(newTime);
  };

  // Auto-scroll text with audio playback
  useEffect(() => {
    if (isAudioPlaying && voiceoverTextRef.current && audioDuration > 0) {
      const progress = audioCurrentTime / audioDuration;
      const scrollHeight = voiceoverTextRef.current.scrollHeight - voiceoverTextRef.current.clientHeight;
      voiceoverTextRef.current.scrollTop = scrollHeight * progress;
    }
  }, [isAudioPlaying, audioCurrentTime, audioDuration]);

  // Auto-play audio when component mounts
  useEffect(() => {
    if (audioRef.current && tour.completion?.debriefAudio) {
      // Small delay to ensure component is mounted
      const timer = setTimeout(() => {
        audioRef.current.play().catch((err) => {
          console.log('Auto-play blocked by browser:', err);
          // User will need to click play button manually
        });
        setIsAudioPlaying(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [tour.completion?.debriefAudio]);

  return (
    <div
      className="fixed z-[9999] flex items-center justify-center"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #0a0f1e 0%, #1a1f2e 100%)',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1.5rem) 1.5rem calc(env(safe-area-inset-bottom, 0px) + 1.5rem)'
      }}
    >
      <style>{`
        @keyframes fadeSlideIn {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.3); }
          50% { box-shadow: 0 0 40px rgba(16, 185, 129, 0.5); }
        }
        .fade-slide-in {
          animation: fadeSlideIn 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .glow-border {
          animation: glow 2s ease-in-out infinite;
        }
      `}</style>

      <div className="fade-slide-in max-w-3xl w-full">
        {/* Mission Report Card */}
        <div
          className="rounded-2xl p-8 md:p-12"
          style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 1px rgba(16, 185, 129, 0.3)',
          }}
        >
          {/* Header Section */}
          <div className="border-b border-gray-700/50 pb-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-1">
                  Mission Status
                </div>
                <h1 className="text-2xl md:text-3xl font-bold font-mono tracking-wide text-emerald-400">
                  COMPLETE
                </h1>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-1">
                  Time Elapsed
                </div>
                <div className="text-2xl font-mono font-bold text-white">
                  {formatTime(timer.elapsedSeconds)}
                </div>
              </div>
            </div>
          </div>

          {/* Target Section */}
          <div className="mb-8">
            <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">
              {tour.collectionMode === "attributes" ? "Target Artifact Identified" : "Target Location Identified"}
            </div>
            <div
              className="glow-border rounded-xl p-6 text-center"
              style={{
                background: 'rgba(16, 185, 129, 0.05)',
                border: '2px solid rgba(16, 185, 129, 0.3)',
              }}
            >
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-mono tracking-wide md:tracking-widest text-emerald-400 mb-2 break-words">
                {finalAnswer}
              </div>
              <div className="text-sm font-mono text-gray-400">
                {tour.collectionMode === "attributes" ? "TARGET CONFIRMED" : "LOCATION CONFIRMED"}
              </div>
            </div>
          </div>

          {/* HQ Transmission - Debrief with Audio */}
          <div className="mb-8">
            <div
              className="rounded-lg p-5"
              style={{
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid rgba(71, 85, 105, 0.3)',
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-mono text-emerald-400 uppercase tracking-wider">
                  HQ Transmission
                </div>
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                  <span>Turn volume up</span>
                </div>
              </div>

              {/* Audio Player - only show if audio file is provided */}
              {tour.completion?.debriefAudio && (
                <>
                  <div
                    className="mb-3 p-2 rounded-lg"
                    style={{
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(71, 85, 105, 0.5)',
                    }}
                  >
                    {/* Audio Controls */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={toggleAudio}
                        className="flex items-center justify-center shrink-0"
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '4px',
                          background: isAudioPlaying ? '#10B981' : '#475569',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'opacity 200ms',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.8';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        aria-label={isAudioPlaying ? 'Pause audio' : 'Play audio'}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          {isAudioPlaying ? (
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                          ) : (
                            <path d="M8 5v14l11-7z" />
                          )}
                        </svg>
                      </button>
                      <div className="flex-1">
                        {/* Progress Bar */}
                        <div
                          onClick={handleSeek}
                          className="relative h-1.5 rounded-full cursor-pointer"
                          style={{
                            background: 'rgba(71, 85, 105, 0.5)',
                          }}
                        >
                          <div
                            className="absolute top-0 left-0 h-full rounded-full transition-all"
                            style={{
                              width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%`,
                              background: '#10B981',
                            }}
                          />
                        </div>
                      </div>
                      {/* Time Display */}
                      <div className="text-xs font-mono shrink-0" style={{ color: '#9ca3af' }}>
                        {formatTime(audioCurrentTime)} / {formatTime(audioDuration)}
                      </div>
                    </div>
                  </div>
                  <audio
                    ref={audioRef}
                    src={tour.completion.debriefAudio}
                    onEnded={handleAudioEnded}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    preload="metadata"
                  />
                </>
              )}

              {/* Scrolling Debrief Text */}
              <div
                ref={voiceoverTextRef}
                className="text-lg text-gray-300 leading-relaxed overflow-y-auto"
                style={{
                  maxHeight: '200px',
                  scrollBehavior: 'smooth'
                }}
              >
                {tour.completion?.debriefText ? (
                  <p className="italic" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: `"${tour.completion.debriefText}"` }} />
                ) : tour.completion?.voiceover?.intro ? (
                  <p className="italic" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: `"${tour.completion.voiceover.intro}"` }} />
                ) : (
                  <p>Mexican Heritage Authority has been notified and recovery teams are en route to Xochimilco. Thanks to your efforts, Montezuma's treasure will be secured and preserved for future generations.</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={onComplete}
            className="w-full py-4 rounded-xl font-semibold uppercase tracking-wider text-sm transition-all"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(16, 185, 129, 0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(16, 185, 129, 0.25)';
            }}
          >
            Continue to Debrief →
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mission Complete Screen - final reveal with staged animations
 */
function MissionCompleteScreen({ tour, timer, onNext }) {
  const [stage, setStage] = useState(0); // 0: intro, 1-5: recovery stages, 6: complete

  // Debug: Log tour completion data
  useEffect(() => {
    console.log('[MissionComplete] Tour completion data:', tour.completion);
    console.log('[MissionComplete] Has voiceover?', !!tour.completion?.voiceover);
    console.log('[MissionComplete] Has epilogue?', !!tour.completion?.epilogue);
  }, [tour]);

  // Pause the timer when the mission complete screen appears
  useEffect(() => {
    if (timer && timer.pauseTimer) {
      console.log('[MissionComplete] Pausing timer');
      timer.pauseTimer();
    }
  }, [timer]);

  // Get recovery stages from tour data or use fallback
  const recoveryStages = tour.completion?.recoveryStages || [
    { status: "Transmitting...", complete: "Transmitted" },
    { status: "Dispatching team...", complete: "Team dispatched" },
    { status: "Securing site...", complete: "Site secured" },
    { status: "Recovering artifacts...", complete: "Artifacts recovered" }
  ];

  useEffect(() => {
    console.log('[MissionComplete] Stage changed to:', stage);
  }, [stage]);

  useEffect(() => {
    console.log('[MissionComplete] Starting stage progression');
    // Stage 0 -> 1: Show intro voiceover immediately
    const timer0 = setTimeout(() => {
      console.log('[MissionComplete] Advancing to stage 1');
      setStage(1);
    }, 1000);

    // Stage 1 -> 2-5: Progress through recovery stages
    const timer1 = setTimeout(() => {
      console.log('[MissionComplete] Advancing to stage 2');
      setStage(2);
    }, 4000);
    const timer2 = setTimeout(() => {
      console.log('[MissionComplete] Advancing to stage 3');
      setStage(3);
    }, 6500);
    const timer3 = setTimeout(() => {
      console.log('[MissionComplete] Advancing to stage 4');
      setStage(4);
    }, 9000);
    const timer4 = setTimeout(() => {
      console.log('[MissionComplete] Advancing to stage 5');
      setStage(5);
    }, 11500);

    // Stage 5 -> 6: Complete with final voiceover
    const timer5 = setTimeout(() => {
      console.log('[MissionComplete] Advancing to stage 6 (complete)');
      setStage(6);
    }, 14000);

    return () => {
      clearTimeout(timer0);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearTimeout(timer5);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6 mission-complete-fade-in">
      <style>{`
        .mission-complete-fade-in {
          animation: missionFadeIn 0.8s ease-out forwards;
        }
        @keyframes missionFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .decrypt-text {
          animation: decrypt 0.8s ease-out forwards;
        }
        @keyframes decrypt {
          0% {
            opacity: 0;
            filter: blur(8px);
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            filter: blur(0);
            transform: scale(1);
          }
        }
        .transmit-pulse {
          animation: transmitPulse 1.5s ease-in-out infinite;
        }
        @keyframes transmitPulse {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
        }
        .success-glow {
          animation: successGlow 2s ease-in-out infinite;
        }
        @keyframes successGlow {
          0%, 100% { box-shadow: 0 0 40px rgba(16, 185, 129, 0.3); }
          50% { box-shadow: 0 0 60px rgba(16, 185, 129, 0.5); }
        }
        .slide-up {
          animation: slideUp 0.6s ease-out forwards;
        }
        @keyframes slideUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .check-mark {
          animation: checkMark 0.5s ease-out forwards;
        }
        @keyframes checkMark {
          0% {
            opacity: 0;
            transform: scale(0) rotate(-45deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
      `}</style>

      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-xs font-mono font-bold uppercase tracking-widest text-emerald-400 mb-2">
            {stage >= 6 ? (tour.completion?.title || 'MISSION COMPLETE') : 'PROCESSING...'}
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            {tour.completion?.subtitle || 'LOCATION DECIPHERED'}
          </h1>
          <div className="text-sm text-gray-400">
            Time: {timer.formattedTime} {timer.hasUsedRelief && '(with relief)'}
          </div>
        </div>

        {/* Decrypted location reveal */}
        <div
          className="p-8 rounded-xl mb-6"
          style={{
            background: 'rgba(15, 23, 42, 0.8)',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }}
        >
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-3">
            Transmission: Mission HQ
          </div>

          {/* HQ Voiceover - Intro */}
          {stage >= 1 && tour.completion?.voiceover?.intro && (
            <div className="mb-6 p-4 rounded-lg decrypt-text" style={{
              background: 'rgba(16, 185, 129, 0.05)',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <p className="text-base text-gray-200 leading-relaxed italic">
                "{tour.completion.voiceover.intro}"
              </p>
            </div>
          )}

          {/* Location name reveal */}
          <div className="text-3xl font-bold text-emerald-400 mb-6 font-mono text-center decrypt-text">
            {tour.completion?.revelation || '[LOCATION NAME]'}
          </div>

          {/* Recovery sequence with voiceover stages */}
          <div className="space-y-3">
            {recoveryStages.map((recoveryStage, idx) => {
              const stageNum = idx + 1;
              const isActive = stage >= stageNum;
              const isComplete = stage > stageNum;
              const voiceoverKey = `stage${stageNum}`;
              const voiceoverText = tour.completion?.voiceover?.[voiceoverKey];

              return (
                <div key={idx}>
                  {isActive && (
                    <div className="slide-up">
                      {/* Status indicator */}
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className={`w-3 h-3 rounded-full ${isComplete ? '' : 'transmit-pulse'}`}
                          style={{
                            background: isComplete ? '#10B981' : '#F59E0B',
                            flexShrink: 0
                          }}
                        />
                        <p className="text-sm text-gray-300">
                          {isComplete ? recoveryStage.complete : recoveryStage.status}
                        </p>
                      </div>

                      {/* Voiceover narration for this stage */}
                      {voiceoverText && (
                        <div className="ml-6 mb-3 p-3 rounded" style={{
                          background: 'rgba(16, 185, 129, 0.03)',
                          borderLeft: '2px solid rgba(16, 185, 129, 0.3)'
                        }}>
                          <p className="text-xs text-gray-400 italic">
                            "{voiceoverText}"
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Final voiceover message */}
          {stage >= 6 && tour.completion?.voiceover?.complete && (
            <div className="mt-6 pt-6 border-t border-emerald-500/20 decrypt-text">
              <div className="p-4 rounded-lg" style={{
                background: 'rgba(16, 185, 129, 0.05)',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <p className="text-base text-gray-200 leading-relaxed italic">
                  "{tour.completion.voiceover.complete}"
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Skip button for testing - show always */}
        <div className="text-center mb-4">
          <button
            onClick={() => setStage(6)}
            className="px-4 py-2 rounded-lg font-semibold text-xs uppercase tracking-wide"
            style={{
              background: 'rgba(71, 85, 105, 0.3)',
              color: '#9CA3AF',
              border: '1px solid rgba(71, 85, 105, 0.5)',
              cursor: 'pointer'
            }}
          >
            Skip to End (Debug)
          </button>
        </div>

        {/* Next button - only show when complete */}
        {stage >= 6 && (
          <div className="text-center decrypt-text">
            <button
              onClick={onNext}
              className="px-8 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide font-mono"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Continue →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Historical Epilogue Screen - Educational content about the treasure location
 */
function HistoricalEpilogueScreen({ tour, onNext }) {
  const epilogue = tour.completion?.epilogue;

  // If no epilogue data, skip directly to feedback
  useEffect(() => {
    if (!epilogue) {
      onNext();
    }
  }, [epilogue, onNext]);

  if (!epilogue) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6" style={{
      paddingTop: 'max(1.5rem, calc(env(safe-area-inset-top, 0px) + 1.5rem))',
      paddingBottom: 'max(1.5rem, calc(env(safe-area-inset-bottom, 0px) + 1.5rem))'
    }}>
      <style>{`
        .epilogue-fade-in {
          animation: epilogueFadeIn 0.6s ease-out forwards;
        }
        @keyframes epilogueFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .section-appear {
          animation: sectionAppear 0.5s ease-out forwards;
        }
        @keyframes sectionAppear {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="w-full max-w-3xl mx-auto epilogue-fade-in">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-xs font-mono font-bold uppercase tracking-widest text-emerald-400 mb-2">
            Historical Context
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
            {epilogue.title}
          </h1>
          <p className="text-lg text-gray-400">
            {epilogue.subtitle}
          </p>
        </div>

        {/* Content sections */}
        <div className="space-y-6 mb-8">
          {epilogue.sections.map((section, idx) => (
            <div
              key={idx}
              className="p-6 rounded-xl section-appear"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                animationDelay: `${idx * 0.1}s`
              }}
            >
              <h3 className="text-lg font-bold text-emerald-400 mb-3">
                {section.heading}
              </h3>
              <p className="text-base text-gray-300 leading-relaxed">
                {section.content}
              </p>
            </div>
          ))}
        </div>

        {/* Call to action */}
        {epilogue.callToAction && (
          <div
            className="p-6 rounded-xl mb-6 text-center section-appear"
            style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              animationDelay: `${epilogue.sections.length * 0.1}s`
            }}
          >
            <p className="text-base text-emerald-300 mb-4">
              {epilogue.callToAction}
            </p>
            {epilogue.googleMapsLink && (
              <a
                href={epilogue.googleMapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-2 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  color: '#10B981'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(16, 185, 129, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
                }}
              >
                View on Google Maps →
              </a>
            )}
          </div>
        )}

        {/* Continue button */}
        <div className="text-center">
          <button
            onClick={onNext}
            className="px-8 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide font-mono"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Answer Tracker Modal - shows decoded letters with animation
 */
function AnswerTrackerModal({ tour, progressHook, justSolvedId, onClose }) {
  const [showLetterExtraction, setShowLetterExtraction] = useState(true);
  const [extractedLetter, setExtractedLetter] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // Get all solved steps
  const solvedSteps = tour.steps.filter(step =>
    progressHook.isAnsweredCorrect(step) === true
  );

  // Find the step that was just solved
  const justSolvedStep = tour.steps.find(step => step.id === justSolvedId);

  useEffect(() => {
    if (justSolvedStep && justSolvedStep.revealedLetter) {
      // Show letter extraction animation
      setExtractedLetter({
        answer: justSolvedStep.solution,
        letter: justSolvedStep.revealedLetter,
        position: justSolvedStep.secretIndex
      });

      // After 3 seconds, hide extraction and show grid (increased from 1.5s for better comprehension)
      const timer = setTimeout(() => {
        setShowLetterExtraction(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [justSolvedStep]);

  // Check if all fragments have been collected (final location solved)
  const allFragmentsCollected = solvedSteps.length === tour.steps.length;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      // Pass true if all fragments collected to trigger transmission animation
      onClose(allFragmentsCollected);
    }, 300); // Match animation duration
  };

  return (
    <div
      className={`fixed z-50 flex items-center justify-center ${isClosing ? 'modal-fade-out' : 'modal-fade-in'}`}
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(8px)',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1rem) 1rem calc(env(safe-area-inset-bottom, 0px) + 1rem)'
      }}
      onClick={handleClose}
    >
      <div
        className={`w-full max-w-md rounded-xl p-6 ${isClosing ? 'modal-scale-out' : ''}`}
        style={{
          background: '#1e293b',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          boxShadow: '0 0 40px rgba(16, 185, 129, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          .modal-fade-in {
            animation: modalFadeIn 0.3s ease-out;
          }
          .modal-fade-out {
            animation: modalFadeOut 0.3s ease-out forwards;
          }
          .modal-scale-out {
            animation: modalScaleOut 0.3s ease-out forwards;
          }
          @keyframes modalFadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          @keyframes modalFadeOut {
            from {
              opacity: 1;
            }
            to {
              opacity: 0;
            }
          }
          @keyframes modalScaleOut {
            from {
              transform: scale(1);
              opacity: 1;
            }
            to {
              transform: scale(0.95);
              opacity: 0;
            }
          }
          .letter-extract {
            animation: boxHighlight 2s ease-out forwards;
          }
          @keyframes boxHighlight {
            0% {
              transform: scale(1);
              box-shadow: 0 0 0 rgba(16, 185, 129, 0);
            }
            30% {
              transform: scale(1);
              box-shadow: 0 0 0 rgba(16, 185, 129, 0);
            }
            50% {
              transform: scale(1.1);
              box-shadow: 0 0 20px rgba(16, 185, 129, 0.5);
            }
            70% {
              transform: scale(1.05);
              box-shadow: 0 0 20px rgba(16, 185, 129, 0.5);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
            }
          }
          .answer-word {
            animation: fadeIn 0.5s ease-out forwards;
          }
          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: translateY(10px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .transmit-pulse {
            animation: transmitPulse 1.5s ease-in-out infinite;
          }
          @keyframes transmitPulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 0 30px rgba(239, 68, 68, 0.5);
            }
            50% {
              transform: scale(1.03);
              box-shadow: 0 0 40px rgba(239, 68, 68, 0.8);
            }
          }
        `}</style>

        {/* Letter Extraction Animation */}
        {showLetterExtraction && extractedLetter ? (
          <div className="text-center py-8">
            <div className="text-xs font-mono font-bold uppercase tracking-widest text-emerald-400 mb-8">
              Fragment Extracted
            </div>

            {/* Show the answer with highlighted letter in a box */}
            <div className="mb-10 answer-word">
              <div className="flex justify-center items-center gap-1 font-mono text-3xl font-bold">
                {extractedLetter.answer.split('').map((char, idx) => {
                  // Find the first occurrence of the extracted letter
                  const firstOccurrenceIndex = extractedLetter.answer.toUpperCase().indexOf(extractedLetter.letter.toUpperCase());
                  const isExtracted = idx === firstOccurrenceIndex;

                  return isExtracted ? (
                    <div
                      key={idx}
                      className="letter-extract w-12 h-14 sm:w-14 sm:h-16 flex items-center justify-center rounded border-2"
                      style={{
                        borderColor: '#10B981',
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        color: '#10B981',
                        fontWeight: 'bold',
                        letterSpacing: '0.05em'
                      }}
                    >
                      {char}
                    </div>
                  ) : (
                    <span
                      key={idx}
                      style={{
                        color: '#9CA3AF',
                        fontWeight: 'bold',
                        letterSpacing: '0.05em',
                        display: 'inline-block'
                      }}
                    >
                      {char}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Use CoordinateDecoder component */}
            <div className="mb-6">
              <CoordinateDecoder
                tour={tour}
                solvedSteps={solvedSteps}
                justSolvedStep={justSolvedStep}
              />
            </div>

            {/* Success message when all fragments collected */}
            {allFragmentsCollected && (
              <div className="mb-6 p-4 rounded-lg decrypt-text" style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}>
                <div className="text-center">
                  <div className="text-xs font-mono uppercase tracking-widest text-red-400 mb-2">
                    ✓ Location Deciphered
                  </div>
                  <div className="text-base font-semibold text-white">
                    You've cracked the cipher and revealed the location of Montezuma's treasure.
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleClose}
              className={`w-full py-3 rounded-lg font-semibold uppercase tracking-wide text-sm ${allFragmentsCollected ? 'transmit-pulse' : ''}`}
              style={{
                background: allFragmentsCollected
                  ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                border: 'none',
                boxShadow: allFragmentsCollected
                  ? '0 0 30px rgba(239, 68, 68, 0.5)'
                  : 'none'
              }}
            >
              {allFragmentsCollected ? 'Transmit to HQ' : 'Continue Mission'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
